import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';

/* ─── helpers ─── */
const formatTime = (s) => {
  if (!s || isNaN(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, '0');
  return `${m}:${sec}`;
};

const seeded = (seed) => {
  let s = seed;
  return () => { s = (s * 16807 + 0) % 2147483647; return (s - 1) / 2147483646; };
};

const generateBars = (url = '', count = 32) => {
  const rand = seeded(url.split('').reduce((a, c) => a + c.charCodeAt(0), 1));
  return Array.from({ length: count }, (_, i) => {
    const env = Math.sin((i / count) * Math.PI);
    return Math.max(0.08, Math.min(0.9, 0.1 + rand() * 0.65 * env + 0.06));
  });
};

/* ─── SmartAudioPlayer ─── */
const SmartAudioPlayer = ({ audioUrl, duration, sx = {} }) => {
  const [isPlaying, setIsPlaying]     = useState(false);
  const [progress, setProgress]       = useState(0);
  const [currentTime, setCurrentTime] = useState('0:00');
  const [bars]                        = useState(() => generateBars(audioUrl, 32));
  const audioRef = useRef(null);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => {
      const dur = el.duration || 0;
      setCurrentTime(formatTime(el.currentTime));
      setProgress(dur ? (el.currentTime / dur) * 100 : 0);
    };
    const onEnd = () => { setIsPlaying(false); setProgress(0); setCurrentTime('0:00'); };
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('ended', onEnd);
    return () => { el.removeEventListener('timeupdate', onTime); el.removeEventListener('ended', onEnd); };
  }, []);

  const togglePlay = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    if (isPlaying) { el.pause(); setIsPlaying(false); }
    else { el.play().catch(console.error); setIsPlaying(true); }
  }, [isPlaying]);

  const seekToBar = (i) => {
    const el = audioRef.current;
    if (!el || !el.duration) return;
    el.currentTime = (i / bars.length) * el.duration;
    setProgress((i / bars.length) * 100);
  };

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        px: 1,
        py: 0.6,
        borderRadius: '8px',
        background: '#fff',
        border: '1px solid #E8F5E9',
        minWidth: 160,
        maxWidth: 220,
        ...sx,
      }}
    >
      <audio ref={audioRef} src={audioUrl} preload="metadata" />

      {/* Play / Pause button */}
      <Box
        onClick={togglePlay}
        sx={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          background: '#16A34A',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          flexShrink: 0,
          transition: 'box-shadow 0.15s, background 0.15s',
          boxShadow: isPlaying
            ? '0 0 0 3px #DCFCE7'
            : '0 1px 4px rgba(22,163,74,0.3)',
          '&:hover': { background: '#15803D' },
        }}
      >
        {isPlaying
          ? <PauseIcon    sx={{ fontSize: 12, color: '#fff' }} />
          : <PlayArrowIcon sx={{ fontSize: 14, color: '#fff', ml: '1px' }} />
        }
      </Box>

      {/* Waveform — slides in when playing, collapses when paused */}
      <Box
        sx={{
          overflow: 'hidden',
          maxWidth: isPlaying ? '120px' : '0px',
          opacity: isPlaying ? 1 : 0,
          transition: 'max-width 0.3s ease, opacity 0.25s ease',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '120px',
            height: 24,
            gap: '1.5px',
            cursor: 'pointer',
          }}
        >
          {bars.map((h, i) => {
            const played = (i / bars.length) * 100 <= progress;
            const isHead = Math.abs((i / bars.length) * 100 - progress) < (100 / bars.length);
            return (
              <Box
                key={i}
                onClick={() => seekToBar(i)}
                sx={{
                  flex: 1,
                  borderRadius: '1px',
                  height: `${Math.max(6, Math.round(h * 65))}%`,
                  minHeight: '2px',
                  maxHeight: '65%',
                  alignSelf: 'center',
                  background: isHead
                    ? '#4ADE80'
                    : played
                    ? '#16A34A'
                    : '#D1FAE5',
                  transition: 'background 0.06s',
                  ...(isPlaying && played && !isHead && {
                    animation: `sw ${(0.5 + (i % 5) * 0.07).toFixed(2)}s ease-in-out infinite alternate`,
                    animationDelay: `${((i / bars.length) * 0.28).toFixed(2)}s`,
                  }),
                  '@keyframes sw': {
                    '0%':   { transform: 'scaleY(0.7)',  opacity: 0.85 },
                    '100%': { transform: 'scaleY(1.25)', opacity: 1 },
                  },
                }}
              />
            );
          })}
        </Box>
      </Box>

      {/* Time */}
      <Typography
        sx={{
          fontSize: '0.65rem',
          fontWeight: 600,
          color: '#374151',
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
          flexShrink: 0,
          letterSpacing: '0.01em',
          ml: isPlaying ? 0 : 0.25,
          transition: 'margin 0.3s ease',
        }}
      >
        {currentTime}
        <Box component="span" sx={{ fontWeight: 400, color: '#9CA3AF', mx: '2px' }}>/</Box>
        {duration || '0:00'}
      </Typography>
    </Box>
  );
};

export default SmartAudioPlayer;
